import { z } from 'zod'
import { LastNameSchema } from '../../domains/last-name'
import { NameSchema } from '../../domains/name'
import { optionalString, requiredString } from '../../primitives'
import { MESSAGES } from '../../utils/messages'

/**
 * Declaración de Persona Expuesta Políticamente (PEP). Requisito de la Intendencia
 * de Verificación Especial (IVE). Espejo de la forma interna (web shared/schemas/
 * pep*, pep-person, pep-relative, pep-partner): 4 banderas sí/no y, por cada una,
 * el detalle correspondiente.
 *
 * Datos de cumplimiento → fuente ÚNICA acá (front + API) y persistencia
 * estructurada en Mongoose (NO Mixed), igual que el applicant.
 *
 * Catálogos (parentesco, asociación, origen de riqueza, condición migratoria) se
 * validan como string requerido —el catálogo lo posee el front—, igual que internamente.
 */

/** Campos comunes a toda entrada PEP: dónde trabaja la persona con cargo público. */
const PepEntryBase = z.object({
  id: optionalString(),
  institutionWork: requiredString(),
  position: requiredString(),
  country: requiredString()
})

/** El propio solicitante es PEP: detalle de su cargo + origen de su riqueza. */
export const PersonPepSchema = PepEntryBase.extend({
  originWealth: z.array(requiredString()).min(1, MESSAGES.REQUIRED),
  otherOriginWealth: optionalString()
})

/** Un familiar del solicitante es PEP. */
export const RelativePepSchema = PepEntryBase.extend({
  relationship: requiredString(),
  otherRelationship: optionalString(),
  name: NameSchema,
  lastName: LastNameSchema,
  gender: requiredString(),
  migrationStatus: requiredString()
})

/** Un socio/asociado del solicitante es PEP. */
export const PartnerPepSchema = PepEntryBase.extend({
  association: requiredString(),
  otherAssociation: optionalString(),
  name: NameSchema,
  lastName: LastNameSchema,
  gender: requiredString(),
  migrationStatus: requiredString()
})

const base = z.object({
  /** El solicitante es PEP. */
  isPep: z.boolean().default(false),
  pep: z.array(PersonPepSchema).default([]),
  /** Tiene familiares PEP. */
  hasRelativesPep: z.boolean().default(false),
  relativesPep: z.array(RelativePepSchema).default([]),
  /** Tiene socios/asociados PEP. */
  hasPartnersPep: z.boolean().default(false),
  partnersPep: z.array(PartnerPepSchema).default([]),
  /** Es contratista o proveedor del Estado (CPE). */
  isCPE: z.boolean().default(false)
})

/**
 * Schema ESTRICTO (submit). Cada bandera en `Sí` exige al menos un registro; los
 * registros presentes se validan campo a campo por los element schemas de arriba.
 * El auto-save NO usa esto (guarda parciales); la validación dura es en submit.
 */
export const PepDeclaration = base.superRefine((data, ctx) => {
  if (data.isPep && data.pep.length === 0) {
    ctx.addIssue({ path: ['pep'], code: z.ZodIssueCode.custom, message: MESSAGES.REQUIRED })
  }
  if (data.hasRelativesPep && data.relativesPep.length === 0) {
    ctx.addIssue({
      path: ['relativesPep'],
      code: z.ZodIssueCode.custom,
      message: MESSAGES.REQUIRED
    })
  }
  if (data.hasPartnersPep && data.partnersPep.length === 0) {
    ctx.addIssue({
      path: ['partnersPep'],
      code: z.ZodIssueCode.custom,
      message: MESSAGES.REQUIRED
    })
  }
})

/**
 * La forma de la declaración PEP SIN las reglas cruzadas.
 *
 * `PepDeclaration` es un schema de ENTRADA: su `superRefine` exige coherencia entre
 * campos al guardar. Un contrato de LECTURA devuelve lo que hay guardado —
 * incluidos los expedientes viejos que se escribieron antes de que esa regla
 * existiera—, así que consume la base y no la versión refinada.
 */
export const PepDeclarationShape = base

export type PersonPep = z.infer<typeof PersonPepSchema>
export type RelativePep = z.infer<typeof RelativePepSchema>
export type PartnerPep = z.infer<typeof PartnerPepSchema>
export type PepDeclaration = z.infer<typeof base>
